import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Clock, Users, Printer, UtensilsCrossed, Pencil, Layers, ChevronRight } from "lucide-react";
import { unitLabel } from "@/lib/recipeCalc";
import PrintDialog from "@/components/recipes/PrintDialog";
import { api } from "@/lib/api";
import { num } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ErrorState, Loading, Page, PageHeader } from "@/components/kit";
import { AuthImage } from "@/components/conference/PhotoCapture";

function IngredientLine({ i }) {
  const nav = useNavigate();
  const base = i.kind === "recipe" && i.recipe_id;
  return (
    <div className={`flex items-baseline gap-3 py-3 text-lg ${base ? "cursor-pointer" : ""}`} onClick={base ? () => nav(`/recipes/${i.recipe_id}/kitchen`) : undefined} data-testid={`kitchen-ingredient-${i.id}`}>
      <span className="flex-1 font-semibold">
        {i.product_name}
        {base && <span className="badge badge-brand !text-xs ml-2 align-middle"><Layers size={11} /> Preparo base <ChevronRight size={11} /></span>}
      </span>
      <span className="tnum font-extrabold">{num(i.qty_net)} {unitLabel(i.unit)}</span>
    </div>
  );
}

function Ingredients({ items }) {
  return (
    <div className="card" data-testid="kitchen-ingredients">
      <div className="h2 mb-3">Ingredientes</div>
      <div className="divide">
        {items.map((i) => <IngredientLine key={i.id} i={i} />)}
        {!items.length && <p className="caption py-2">Sem ingredientes cadastrados.</p>}
      </div>
      <p className="caption mt-2">Quantidades líquidas (já descontadas as perdas de pré-preparo).</p>
    </div>
  );
}

function Steps({ steps }) {
  return (
    <div className="card" data-testid="kitchen-steps">
      <div className="h2 mb-4">Modo de preparo</div>
      <ol className="flex flex-col gap-4">
        {steps.map((s, i) => (
          <li key={`${i}-${s.slice(0, 20)}`} className="flex gap-4 text-lg leading-relaxed">
            <span className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold shrink-0" style={{ background: "var(--brand)" }}>{i + 1}</span>
            <span className="pt-1 whitespace-pre-wrap">{s}</span>
          </li>
        ))}
        {!steps.length && <p className="caption">Sem etapas cadastradas.</p>}
      </ol>
    </div>
  );
}

export default function RecipeKitchen() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isMgmt } = useAuth();
  const [printing, setPrinting] = useState(false);
  const { data: r, isLoading, isError, error } = useQuery({ queryKey: ["recipe", id], queryFn: () => api(`/recipes/${id}`), retry: false });
  if (isLoading) return <Loading />;
  if (isError) return <Page><PageHeader title="Ficha técnica" back="/recipes" /><ErrorState message={error.message} /></Page>;
  const right = (
    <div className="flex gap-2 no-print">
      <button className="icon-btn" title="Imprimir" onClick={() => setPrinting(true)} data-testid="kitchen-print-button"><Printer size={17} /></button>
      {isMgmt && <button className="icon-btn" title="Editar" onClick={() => nav(`/recipes/${id}`)} data-testid="kitchen-edit-button"><Pencil size={16} /></button>}
    </div>
  );
  return (
    <Page>
      <PageHeader title={r.name} subtitle={r.menu_category || "Modo cozinha"} back="/recipes" right={right} />
      <div className="flex flex-wrap gap-3 mb-5">
        {r.prep_time_min ? <span className="badge badge-brand !text-sm !py-1.5 !px-3"><Clock size={14} /> {r.prep_time_min} min</span> : null}
        <span className="badge badge-brand !text-sm !py-1.5 !px-3" data-testid="kitchen-yield"><Users size={14} /> Rende {num(r.yield_portions)} porção(ões){r.yield_qty ? ` · ${num(r.yield_qty)} ${r.yield_unit}` : ""}</span>
        {r.is_base && <span className="badge badge-muted !text-sm !py-1.5 !px-3" data-testid="kitchen-base-badge"><Layers size={14} /> Preparo base</span>}
      </div>
      <PrintDialog open={printing} onOpenChange={setPrinting} recipes={[r]} />
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-5 items-start">
        <div className="flex flex-col gap-5">
          <div className="rounded-2xl overflow-hidden aspect-[4/3] relative" style={{ background: "var(--surface-alt)" }}>
            {r.photo_path ? <AuthImage path={r.photo_path} className="absolute inset-0 w-full h-full" testid="kitchen-photo" /> : <div className="absolute inset-0 flex items-center justify-center muted"><UtensilsCrossed size={44} /></div>}
          </div>
          <Ingredients items={r.ingredients} />
        </div>
        <div className="flex flex-col gap-5">
          <Steps steps={r.steps || []} />
          {(r.plating_notes || r.plating_photo_path) && (
            <div className="card flex flex-col gap-3" data-testid="kitchen-plating">
              <div className="h2">Empratamento</div>
              {r.plating_notes && <p className="text-lg leading-relaxed whitespace-pre-wrap">{r.plating_notes}</p>}
              {r.plating_photo_path && <AuthImage path={r.plating_photo_path} className="w-full aspect-[4/3] rounded-xl" testid="kitchen-plating-photo" />}
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}
